import React, { useState } from 'react';
import { Complaint } from '../../types/complaint';
import { ActivityEntry, CreateActivityDto } from '../../types/activity';
import { StatusBadge, PriorityBadge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { ActivityTimeline } from '../../components/activity/ActivityTimeline';
import { CommentForm } from '../../components/activity/CommentForm';
import { AttachmentManager } from '../../components/attachment/AttachmentManager';
import { SLABadge } from '../../components/sla/SLABadge';
import { ArrowLeft, MessageSquare, Paperclip, UserCheck, ShieldCheck, Wrench } from 'lucide-react';

interface ComplaintDetailPageProps {
  complaint: Complaint;
  onBack: () => void;
}

const DEMO_ACTIVITIES: ActivityEntry[] = [
  {
    id: 'ACT-001',
    entity_type: 'complaint',
    entity_id: 'CMP-2026-00001',
    activity_type: 'status_change',
    author_id: 'USR-00001',
    author_name: 'System',
    author_role: 'ADMIN',
    content: 'Complaint created and submitted for review',
    mentions: [],
    attachments: [],
    is_internal: false,
    created_at: '2026-09-10T08:15:00Z',
    system_meta: { field: 'status', old_value: '', new_value: 'New' },
  },
  {
    id: 'ACT-002',
    entity_type: 'complaint',
    entity_id: 'CMP-2026-00001',
    activity_type: 'comment',
    author_id: 'USR-00002',
    author_name: 'Alex Mercer',
    author_role: 'DEPARTMENT_USER',
    content: 'Server room temperature rising rapidly. Multiple equipment alerts triggered. @Rajesh Kumar please review urgently.',
    mentions: [{ user_id: 'USR-00003', user_name: 'Rajesh Kumar' }],
    attachments: [],
    is_internal: false,
    created_at: '2026-09-10T08:20:00Z',
  },
  {
    id: 'ACT-003',
    entity_type: 'complaint',
    entity_id: 'CMP-2026-00001',
    activity_type: 'assignment',
    author_id: 'USR-00003',
    author_name: 'Rajesh Kumar',
    author_role: 'OFFICER',
    content: 'Complaint reviewed. Priority upgraded to Critical. Assigned to Supervisor Suresh.',
    mentions: [],
    attachments: [],
    is_internal: false,
    created_at: '2026-09-10T08:30:00Z',
    system_meta: { field: 'priority', old_value: 'High', new_value: 'Critical' },
  },
  {
    id: 'ACT-004',
    entity_type: 'complaint',
    entity_id: 'CMP-2026-00001',
    activity_type: 'internal_note',
    author_id: 'USR-00003',
    author_name: 'Rajesh Kumar',
    author_role: 'OFFICER',
    content: 'Escalation trigger â€” SLA breach imminent. Check vendor availability before assigning.',
    mentions: [],
    attachments: [],
    is_internal: true,
    created_at: '2026-09-10T08:35:00Z',
  },
  {
    id: 'ACT-005',
    entity_type: 'complaint',
    entity_id: 'CMP-2026-00001',
    activity_type: 'comment',
    author_id: 'USR-00004',
    author_name: 'Suresh',
    author_role: 'SUPERVISOR',
    content: 'Site inspected. Compressor E4 fault detected. Assigning to Vendor Alpha Technical Services.',
    mentions: [],
    attachments: [{ id: 'ATT-001', file_name: 'inspection_photo_1.jpg', file_type: 'image/jpeg', data_url: '', uploaded_at: '2026-09-10T11:00:00Z' }],
    is_internal: false,
    created_at: '2026-09-10T11:00:00Z',
  },
];

const AVAILABLE_USERS = [
  { user_id: 'USR-00002', name: 'Alex Mercer' },
  { user_id: 'USR-00003', name: 'Rajesh Kumar' },
  { user_id: 'USR-00004', name: 'Suresh' },
  { user_id: 'USR-00005', name: 'CoolTech Services' },
  { user_id: 'USR-00006', name: 'Ahmad Khan' },
];

export const ComplaintDetailPage: React.FC<ComplaintDetailPageProps> = ({ complaint, onBack }) => {
  const [activeTab, setActiveTab] = useState<'timeline' | 'attachments' | 'work_orders'>('timeline');
  const [activities, setActivities] = useState<ActivityEntry[]>(DEMO_ACTIVITIES);

  const handleAddActivity = (dto: CreateActivityDto) => {
    const newEntry: ActivityEntry = {
      id: `ACT-${Date.now()}`,
      entity_type: dto.entity_type,
      entity_id: dto.entity_id,
      activity_type: dto.activity_type,
      author_id: 'USR-00001',
      author_name: 'Current User',
      author_role: 'ADMIN',
      content: dto.content,
      mentions: dto.mentions,
      attachments: dto.attachments,
      is_internal: dto.is_internal,
      created_at: new Date().toISOString(),
    };
    setActivities(prev => [...prev, newEntry]);
  };

  const commentCount = activities.filter(a => a.activity_type === 'comment' || a.activity_type === 'internal_note').length;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between border-b border-slate-800 pb-4">
        <Button variant="ghost" onClick={onBack} icon={<ArrowLeft className="w-4 h-4" />}>
          Back to Complaint List
        </Button>
        <div className="flex items-center gap-2">
          <StatusBadge status={complaint.status_name} />
          <PriorityBadge priority={complaint.priority_name} />
          <SLABadge createdAt={complaint.created_at} priorityName={complaint.priority_name} />
        </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <span className="font-mono text-xs text-sky-400 font-semibold">{complaint.complaint_id}</span>
            <h1 className="text-xl font-semibold text-slate-100 mt-1">{complaint.title}</h1>
          </div>
          <div className="text-xs text-slate-400">
            <span className="block font-medium text-slate-300">Created {new Date(complaint.created_at).toLocaleDateString()}</span>
            <span>By {complaint.created_by_user_name}</span>
          </div>
        </div>

        <p className="text-sm text-slate-300 bg-slate-950 p-4 rounded-lg border border-slate-800/80">
          {complaint.description}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs pt-2">
          <div className="p-3 bg-slate-950 rounded-lg border border-slate-800/60 flex items-center gap-2.5">
            <UserCheck className="w-4 h-4 text-sky-400" />
            <div>
              <p className="text-[10px] text-slate-500 font-medium">Assigned Officer</p>
              <p className="font-semibold text-slate-200">{complaint.assigned_officer_name || 'Unassigned'}</p>
            </div>
          </div>
          <div className="p-3 bg-slate-950 rounded-lg border border-slate-800/60 flex items-center gap-2.5">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <div>
              <p className="text-[10px] text-slate-500 font-medium">Assigned Supervisor</p>
              <p className="font-semibold text-slate-200">{complaint.assigned_supervisor_name || 'Unassigned'}</p>
            </div>
          </div>
          <div className="p-3 bg-slate-950 rounded-lg border border-slate-800/60 flex items-center gap-2.5">
            <Wrench className="w-4 h-4 text-amber-400" />
            <div>
              <p className="text-[10px] text-slate-500 font-medium">Contractor / Vendor</p>
              <p className="font-semibold text-slate-200">{complaint.assigned_vendor_name || 'Unassigned'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex border-b border-slate-800 gap-2">
        <button
          onClick={() => setActiveTab('timeline')}
          className={`px-4 py-2 text-xs font-semibold rounded-t-lg flex items-center gap-2 ${
            activeTab === 'timeline' ? 'bg-slate-800 text-sky-400 border-b-2 border-sky-400' : 'text-slate-400'
          }`}
        >
          <MessageSquare className="w-4 h-4" /> Activity & Discussion ({commentCount})
        </button>
        <button
          onClick={() => setActiveTab('attachments')}
          className={`px-4 py-2 text-xs font-semibold rounded-t-lg flex items-center gap-2 ${
            activeTab === 'attachments' ? 'bg-slate-800 text-sky-400 border-b-2 border-sky-400' : 'text-slate-400'
          }`}
        >
          <Paperclip className="w-4 h-4" /> Attachments ({complaint.attachment_urls.length})
        </button>
      </div>

      {activeTab === 'timeline' && (
        <Card title="Activity Timeline & Discussion Forum" subtitle="Chronological updates across all roles">
          <ActivityTimeline entries={activities} currentUserRole="ADMIN" />
          <div className="mt-4 pt-4 border-t border-slate-800">
            <CommentForm
              entityType="complaint"
              entityId={complaint.complaint_id}
              currentUser={{ user_id: 'USR-00001', name: 'Current User', role: 'ADMIN' }}
              availableUsers={AVAILABLE_USERS}
              onSubmit={handleAddActivity}
            />
          </div>
        </Card>
      )}

      {activeTab === 'attachments' && (
        <Card title="File & Photo Attachments" subtitle="Upload, preview, and manage files attached to this complaint">
          <AttachmentManager entityType="complaint" entityId={complaint.complaint_id} />
        </Card>
      )}
    </div>
  );
};
